/*
 * The Teams half of Settings → Notifications — session 4 of `DESIGN-teams.md`.
 *
 * Two pieces, deliberately separate:
 *
 *   <TeamsChannelPicker>  — ONE channel for the company, shown once at the top of the tab,
 *                           beside the schedule timezone.
 *   <DigestTeamsRow>      — "Also post it in Microsoft Teams", one per digest card.
 *
 * ⚠️ **THE CHANNEL SITS BESIDE THE TIMEZONE BECAUSE IT IS THE SAME KIND OF SETTING.**
 * `email_timezone` is already one value shared by all three digests. Three per-digest
 * channels would be three chances to leave one unset — and an unset channel posts nothing
 * while its switch reads "On", which is the silent failure this whole feature keeps having
 * to design around. Rob's decision, 24/08/2026.
 *
 * ⚠️ **THE TOGGLE AND THE DIGEST ITSELF ARE INDEPENDENT — ROB'S ANSWER (a).** Turning Teams
 * on must never switch the digest on behind an administrator's back: a daily post appearing
 * in a company channel because somebody picked a channel is how an integration gets removed.
 * **So the row SAYS when it cannot do anything**, rather than silently doing nothing — that
 * sentence is the whole reason (a) is safe to choose.
 */
import React, { useState, useEffect } from 'react';
import { Loader2, MessageSquare, AlertTriangle, Send } from 'lucide-react';
import { teamsApi, digestTestApi, apiErrorMessage } from '../lib/api';
import { testToastSlack } from '../lib/slackDigest';
import { toast } from 'sonner';

/*
 * ⚠️ Loaded once and handed down, not fetched per digest card. Three cards each calling
 * `/teams/channels` on every render of the Notifications tab is three times the work for
 * one answer, and they could disagree mid-save.
 */
export function useTeamsChannels() {
  const [channels, setChannels] = useState(null);

  useEffect(() => {
    let live = true;
    teamsApi
      .channels()
      .then((res) => { if (live) setChannels(res.data || []); })
      .catch(() => { if (live) setChannels([]); })
      .finally(() => {});
    return () => { live = false; };
  }, []);

  return channels;
}

export function TeamsChannelPicker({ channels, value, onChange }) {
  if (channels === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Checking Microsoft Teams&hellip;
      </div>
    );
  }

  /*
   * ⚠️ **NOT AN EMPTY DROPDOWN.** A picker with no options and no explanation reads as a
   * broken feature. It says what is missing and where to fix it — the same rule as the
   * profile card's "add the app first" notice, which was a dead end until Rob caught it.
   */
  if (channels.length === 0) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200"
           data-testid="teams-channel-none">
        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
        {/*
          * ⚠️ **THIS WORDING WAS WRONG UNTIL 24/08/2026 AND ROB CAUGHT IT BY TRYING IT.**
          * It said "open the channel, then + Add an app", which is how you add a TAB.
          * A bot is installed to a **team**, not to a channel — Microsoft's own words:
          * *"Teams bots can be installed into personal chat, group chat, or a team (by
          * default the General channel)."* Following the old sentence leads to a tab
          * picker that does not list HolidayHive at all, which reads as "this app is
          * broken" rather than "wrong menu".
          */}
        <p className="text-xs text-amber-800">
          HolidayHive isn&rsquo;t in any Teams team yet. In Teams, find the team in the
          left-hand list, click the <em>&hellip;</em> beside its name, then
          {' '}<em>Manage team &rarr; Apps &rarr; Upload a custom app</em>. It lands in that
          team&rsquo;s General channel and will appear here afterwards.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="teams-channel-picker">
      {/*
        * "in Teams", 19/10/2026: Rob, testing Slack step 4 on staging — the Slack picker
        * below this one says "in Slack", and this one said only "Post who's off in".
        */}
      <label className="text-sm font-medium mb-2 flex items-center gap-1.5">
        <MessageSquare className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
        Post who&rsquo;s off in Teams
      </label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 px-3 rounded-md border border-input bg-background text-sm w-full max-w-sm"
        data-testid="teams-channel-select"
      >
        {/*
          * ⚠️ An explicit "not chosen" option. Without it the first channel looks selected
          * when nothing is, and an administrator would believe they had configured
          * something they had not.
          */}
        {/*
          * ⚠️ **"" FOR "NO CHANNEL", NEVER null — FOUND 18/10/2026.** The Settings save skips
          * every field that arrives as null (`routers/settings.update_settings`: "if value is
          * not None"), so choosing this option and pressing Save used to keep the old channel,
          * say "saved", and bring it back on reload. "" is saved, and the scheduler reads it
          * as no channel.
          */}
        <option value="">No channel chosen</option>
        {channels.map((c) => (
          <option key={c.channel_id} value={c.channel_id}>{c.label}</option>
        ))}
      </select>
      {/*
        * ⚠️ **THIS SENTENCE EXISTS BECAUSE THE SCREEN IMPLIED SOMETHING UNTRUE.** Rob,
        * 26/08/2026: the "Who should receive this?" toggles sit directly above the Teams
        * row, so they read as governing both — and they do not. A channel's audience is
        * its membership; there is no way to post to "admins only" in a channel everyone
        * can read, and pretending otherwise would be a promise we cannot keep.
        *
        * ⚠️ The control people are actually looking for already exists — it is called
        * "which channel". Naming the private-channel route is the whole fix.
        */}
      <p className="text-xs text-muted-foreground mt-2">
        One channel for the whole company &mdash; the daily, weekly and monthly digests all
        post here. Only channels HolidayHive has been added to are listed.
      </p>
      <p className="text-xs text-muted-foreground mt-2">
        Everyone in this channel sees the digest, whoever the email settings are for. For a
        smaller audience, add HolidayHive to a private channel instead.
      </p>
    </div>
  );
}

/*
 * ⚠️ `withTest={false}` when the page places the test button itself — Slack build step 4 put
 * a Slack row under this one, and the button belongs after BOTH, not between them.
 */
export default function DigestTeamsRow({ kind, enabled, digestEnabled, channelId, channels, onChange,
  withTest = true }) {
  const hasChannel = !!channelId;
  const canPost = hasChannel && digestEnabled;

  /*
   * ⚠️ **THE TEST BUTTON SHOWS EVEN WITH NO TEAMS AT ALL, AND THE TOGGLE DOES NOT.**
   * The first version put the button inside the Teams row, which only renders when a
   * channel exists — so a company that had never touched Teams could not test its own
   * EMAIL digest, which is the thing they actually use. The Teams half is conditional;
   * rehearsing a digest is not.
   */
  const teamsAvailable = Array.isArray(channels) && channels.length > 0;

  if (!teamsAvailable) {
    if (!withTest) return null;
    return (
      <div className="border-t border-border mt-4 pt-4" data-testid={`digest-teams-row-${kind}`}>
        <SendTestDigest kind={kind} />
      </div>
    );
  }

  return (
    <div className="border-t border-border mt-4 pt-4" data-testid={`digest-teams-row-${kind}`}>
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          Also post it in Microsoft Teams
        </span>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={!!enabled}
            onChange={(e) => onChange(e.target.checked)}
            data-testid={`digest-teams-toggle-${kind}`}
          />
          <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer
                          peer-checked:after:translate-x-full after:content-[''] after:absolute
                          after:top-0.5 after:left-[2px] after:bg-white after:rounded-full
                          after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
        </label>
      </div>

      {/*
        * ⚠️ **THE TWO REASONS NOTHING WOULD POST ARE SAID SEPARATELY, AND THAT MATTERS.**
        * "No channel chosen" and "the digest itself is switched off" have different fixes,
        * and one message covering both would send an administrator to the wrong one. This
        * is the sentence that makes Rob's decision (a) safe: independent switches are fine
        * as long as the screen never lets you believe something is happening when it isn't.
        */}
      {enabled && !hasChannel && (
        <p className="text-xs text-amber-700 mt-2" data-testid={`digest-teams-nochannel-${kind}`}>
          Nothing will post yet &mdash; choose a channel at the top of this page.
        </p>
      )}
      {enabled && hasChannel && !digestEnabled && (
        <p className="text-xs text-amber-700 mt-2" data-testid={`digest-teams-nodigest-${kind}`}>
          Nothing will post yet &mdash; this digest is switched off above. Turning it on
          sends the email as well.
        </p>
      )}
      {canPost && (
        <p className="text-xs text-muted-foreground mt-2">
          Posted at the same time as the email, grouped by department.
        </p>
      )}

      {withTest && <SendTestDigest kind={kind} />}
    </div>
  );
}

/*
 * ⚠️ **THIS BUTTON EXISTS BECAUSE THE DIGEST WAS OTHERWISE UNTESTABLE.** Each digest sends
 * once per day and remembers, so on 26/08/2026 all three were spent and the only way to
 * see one reach a Teams channel was `DELETE FROM notification_sends` over SSH — 73 rows.
 * A customer cannot do that, and they would hit it on their first afternoon.
 *
 * ⚠️ **IT SENDS FOR REAL, TO EVERYONE** — Rob's decision (b). That is what makes it a
 * rehearsal rather than a preview, and it is why the button says so plainly before you
 * press it rather than in a tooltip nobody opens.
 */
export function SendTestDigest({ kind }) {
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const res = await digestTestApi.send(`${kind}_digest`);
      const sent = res?.data?.sent ?? 0;
      /*
       * ⚠️ Zero is a real answer — nobody is configured to receive it — and it must read
       * as that rather than as a failure. Same trap as "0 bookings in your calendar".
       *
       * ⚠️ Slack build step 4: the server says in one word what Slack did, and the toast says
       * it too — "Test sent" with nothing in the channel would send an admin looking in the
       * wrong place. A refusal is a warning, not a success with small print.
       */
      const slackWord = res?.data?.slack;
      const slackLine = testToastSlack(slackWord);
      const opts = slackLine ? { description: slackLine } : undefined;
      // ⚠️ Spelled out as toast.success / toast.warning with the words inside the call, so
      // `scripts/gen_message_catalog.py` still finds them for Superadmin → Messages.
      if (slackLine && slackWord !== 'posted') {
        toast.warning(sent === 0
          ? 'Test sent. Nobody is set to receive this digest by email, so only the chat channels were tried.'
          : `Test sent to ${sent} ${sent === 1 ? 'person' : 'people'}.`, opts);
      } else {
        toast.success(sent === 0
          ? 'Test sent. Nobody is set to receive this digest by email, so only the chat channels were tried.'
          : `Test sent to ${sent} ${sent === 1 ? 'person' : 'people'}.`, opts);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Couldn’t send the test.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={send}
        disabled={sending}
        className="text-xs inline-flex items-center gap-1.5 underline text-muted-foreground
                   hover:text-foreground disabled:opacity-50"
        data-testid={`digest-test-send-${kind}`}
      >
        {sending
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <Send className="h-3.5 w-3.5" aria-hidden="true" />}
        Send a test now
      </button>
      <p className="text-xs text-muted-foreground mt-1">
        Sends this digest immediately to everyone who normally receives it, and to Teams or
        Slack if that&rsquo;s switched on. Marked &ldquo;Test&rdquo;, and it won&rsquo;t stop
        today&rsquo;s real one going out.
      </p>
    </div>
  );
}
